import { Layer, Source, useMap } from 'react-map-gl';
import { useSelector } from 'react-redux';
import {
  BLE100_TILE_LAYERS,
  BLE500_TILE_LAYERS,
  DAMAGE_PLAIN_TILE_LAYERS,
  LAYER_CONFIGS,
  WSE100_TILE_LAYERS,
  WSE500_TILE_LAYERS,
} from '../../constants/general';
import { filterSelector } from '../../store/selectors/filters';
import { basemapSelector } from '../../store/selectors/general';
import { getBeforeIdForLayer, getIconBadgeColors } from '../../utils/map';
import { zoomSelector } from '../../store/selectors/map';
import { MAP_ICON_PREFIX } from '../../utils/mapIcons';

const getTileUrl = (layers, zoom, areaName) =>
  layers.find(({ min, max, areaName: a }) => zoom >= min && zoom <= max && a === areaName)?.url ??
  '';

const buildPaintProps = (layerId, config, isVisible, opacity, basemapInfo) => {
  const safeOpacity = opacity ?? 1;
  const vis = isVisible ? safeOpacity : 0;

  if (layerId === 'boundary') {
    return {
      fill: null,
      line: {
        'line-color':
          basemapInfo === 'light' ? '#000000' : basemapInfo === 'dark' ? '#F0F3F4' : '#00FFFF',
        'line-width': basemapInfo === 'satellite' ? 2 : 1.5,
        'line-opacity': 1,
      },
      circle: null,
      symbol: null,
    };
  }

  const isPoint = ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]];
  const iconColors = getIconBadgeColors(basemapInfo);

  return {
    fill: {
      'fill-color': config.colors?.fillColor ?? '#3388ff',
      'fill-opacity': vis * 0.6,
      'fill-outline-color': config.colors?.color ?? '#3388ff',
    },
    line: {
      'line-color': config.colors?.color ?? '#3388ff',
      'line-width': 2,
      'line-opacity': vis,
    },
    // Icon layers draw the circle as a black-and-white badge behind the icon (inverted on satellite)
    circle: config.icon
      ? {
          'circle-color': iconColors.badge,
          'circle-radius': 12,
          'circle-opacity': vis,
          'circle-stroke-width': iconColors.ringWidth,
          'circle-stroke-color': iconColors.ring,
          'circle-stroke-opacity': vis,
        }
      : {
          'circle-color': config.colors?.fillColor ?? '#7f8c8d',
          'circle-radius': 6,
          'circle-opacity': vis,
          'circle-stroke-width': 1,
          'circle-stroke-color': config.colors?.color ?? '#7f8c8d',
        },
    // Points sit on a badge; polygons (e.g. college campuses) get a haloed icon at their label point
    symbol: config.icon
      ? {
          'icon-color': iconColors.glyph,
          'icon-halo-color': iconColors.badge,
          'icon-halo-width': ['case', isPoint, 0, iconColors.haloWidth],
          'icon-opacity': vis,
        }
      : null,
  };
};

const RasterTileLayer = ({ layerId, url, opacity, beforeId }) => {
  if (!url) return null;
  const safeOpacity = opacity ?? 1;

  return (
    <Source id={`raster-source-${layerId}`} type="raster" tiles={[url]} tileSize={256}>
      <Layer
        id={`raster-layer-${layerId}`}
        type="raster"
        paint={{ 'raster-opacity': safeOpacity }}
        beforeId={beforeId}
      />
    </Source>
  );
};

const ImageOverlayLayer = ({ layerId, url, bounds, opacity, beforeId }) => {
  if (!url || !bounds) return null;
  const [[south, west], [north, east]] = bounds;
  const coordinates = [
    [west, north], // top-left
    [east, north], // top-right
    [east, south], // bottom-right
    [west, south], // bottom-left
  ];

  return (
    <Source id={`image-source-${layerId}`} type="image" url={url} coordinates={coordinates}>
      <Layer
        id={`image-layer-${layerId}`}
        type="raster"
        paint={{ 'raster-opacity': opacity }}
        beforeId={beforeId}
      />
    </Source>
  );
};

const GeoJSONLayer = ({ layerId, data, isVisible, opacity, basemapInfo, config, beforeId }) => {
  if (!data) return null;

  const safeOpacity = opacity ?? 1;
  const paint = buildPaintProps(layerId, config, isVisible, safeOpacity, basemapInfo);
  const isBoundary = layerId === 'boundary';

  return (
    <Source id={`geojson-source-${layerId}`} type="geojson" data={data}>
      {paint.fill && (
        <Layer
          id={`${layerId}-fill`}
          type="fill"
          filter={['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]]}
          paint={paint.fill}
          beforeId={beforeId}
        />
      )}

      {/* Line */}
      {paint.line && (
        <Layer
          id={`${layerId}-line`}
          type="line"
          filter={
            isBoundary
              ? ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]]
              : [
                  'in',
                  ['geometry-type'],
                  ['literal', ['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon']],
                ]
          }
          paint={paint.line}
          beforeId={beforeId}
        />
      )}

      {/* Circle */}
      {paint.circle && (
        <Layer
          id={`${layerId}-circle`}
          type="circle"
          filter={['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]]}
          paint={paint.circle}
          beforeId={beforeId}
        />
      )}

      {/* Icon */}
      {paint.symbol && (
        <Layer
          id={`${layerId}-icon`}
          type="symbol"
          filter={[
            'in',
            ['geometry-type'],
            ['literal', ['Point', 'MultiPoint', 'Polygon', 'MultiPolygon']],
          ]}
          layout={{
            'icon-image': `${MAP_ICON_PREFIX}${config.icon}`,
            'icon-size': 1,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          }}
          paint={paint.symbol}
          beforeId={beforeId}
        />
      )}
    </Source>
  );
};

export const LayerRenderer = ({ layerId, data, isVisible, opacity, measureMode }) => {
  const { current: map } = useMap();
  const mapInstance = map?.getMap?.();
  const beforeId = getBeforeIdForLayer(layerId, mapInstance);
  const basemapInfo = useSelector(basemapSelector);
  const filterInfo = useSelector(filterSelector);
  const config = LAYER_CONFIGS[layerId];
  const zoom = useSelector(zoomSelector);

  if (!data || !isVisible || !config) return null;

  if (config.mapType === 'imageOverlay') {
    if (layerId === 'combinedSurge') {
      return (
        <>
          {Object.entries(data)
            .reverse()
            .map(([category, categoryData]) =>
              categoryData ? (
                <ImageOverlayLayer
                  key={`${layerId}-${category}`}
                  layerId={`${layerId}-${category}`}
                  url={categoryData.image}
                  bounds={categoryData.bounds}
                  opacity={isVisible ? opacity : 0}
                  beforeId={beforeId}
                />
              ) : null,
            )}
        </>
      );
    }

    const tileUrlMap = {
      bleDep100: BLE100_TILE_LAYERS,
      bleDep500: BLE500_TILE_LAYERS,
      wse100: WSE100_TILE_LAYERS,
      wse500: WSE500_TILE_LAYERS,
      damagePlain: DAMAGE_PLAIN_TILE_LAYERS,
    };

    if (tileUrlMap[layerId]) {
      const url = getTileUrl(tileUrlMap[layerId], zoom, filterInfo.areaName);
      return (
        <RasterTileLayer
          layerId={layerId}
          url={url}
          opacity={isVisible ? opacity : 0}
          beforeId={beforeId}
        />
      );
    }
    // Fallback single image overlay
    return (
      <ImageOverlayLayer
        layerId={layerId}
        url={data.image}
        bounds={data.bounds}
        opacity={isVisible ? opacity : 0}
        beforeId={beforeId}
      />
    );
  }

  if (config.mapType === 'geojson') {
    return (
      <GeoJSONLayer
        layerId={layerId}
        data={data}
        isVisible={isVisible}
        opacity={opacity}
        basemapInfo={basemapInfo}
        config={config}
        beforeId={beforeId}
      />
    );
  }

  return null;
};
